import { useState, useCallback, useRef } from 'react';
import { DateInput } from '@mantine/dates';
import type { DateInputProps } from '@mantine/dates';
import type { CalendarLevel } from '@mantine/dates';
import type { DateStringValue } from '@mantine/dates';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

type AyoDateInputProps = DateInputProps & {
  // Mantine DateInput does not support presets; keep for compatibility with previous API.
  presets?: Array<{ value: string; label: string }>;
};

function isSameDay(a: Date | string | null, b: Date | string | null): boolean {
  if (!a || !b) return false;
  const d1 = dayjs(a);
  const d2 = dayjs(b);
  return d1.isSame(d2, 'day');
}

export function AyoDateInput(props: AyoDateInputProps) {
  const {
    date: controlledDate,
    defaultDate,
    onDateChange,
    getDayProps: propsGetDayProps,
    getMonthControlProps: propsGetMonthControlProps,
    getYearControlProps: propsGetYearControlProps,
    presets: _propsPresets,
    renderDay: propsRenderDay,
    monthsListFormat,
    ...rest
  } = props;

  const initialDate = controlledDate ?? defaultDate ?? new Date();
  const [internalDate, setInternalDate] = useState<Date>(() =>
    initialDate instanceof Date ? initialDate : new Date(initialDate)
  );
  const levelRef = useRef<CalendarLevel>('month');
  const dateRef = useRef(internalDate);
  dateRef.current =
    controlledDate !== undefined
      ? controlledDate instanceof Date
        ? controlledDate
        : new Date(controlledDate)
      : internalDate;
  const displayDate =
    controlledDate !== undefined
      ? controlledDate instanceof Date
        ? controlledDate
        : new Date(controlledDate)
      : internalDate;

  const handleDateChange = useCallback(
    (value: string) => {
      // Парсим строго в формате YYYY-MM-DD как локальную полночь: new Date("YYYY-MM-DD")
      // трактует вход как UTC-полночь, что в положительных таймзонах смещает локальный день.
      const parsed = dayjs(value, 'YYYY-MM-DD', true);
      const newDate = parsed.isValid() ? parsed.startOf('day').toDate() : new Date(value);
      if (isNaN(newDate.getTime())) return;
      if (levelRef.current === 'year') {
        const prev = dateRef.current;
        const merged = new Date(newDate.getFullYear(), prev.getMonth(), 1);
        if (controlledDate === undefined) setInternalDate(merged);
        onDateChange?.(dayjs(merged).format('YYYY-MM-DD'));
      } else {
        if (controlledDate === undefined) setInternalDate(newDate);
        onDateChange?.(value);
      }
    },
    [controlledDate, onDateChange]
  );

  const handleLevelChange = useCallback(
    (level: CalendarLevel) => {
      levelRef.current = level;
      props.onLevelChange?.(level);
    },
    [props.onLevelChange]
  );

  const today = dayjs().toDate();

  const getDayProps: DateInputProps['getDayProps'] = useCallback(
    (date: DateStringValue) => {
      const base = propsGetDayProps?.(date) ?? {};
      if (isSameDay(date, today)) {
        return {
          ...base,
          style: {
            ...base.style,
            fontWeight: 700,
            outline: '2px solid var(--mantine-color-blue-filled)',
            outlineOffset: 1,
          },
        };
      }
      return base;
    },
    [propsGetDayProps]
  );

  const getMonthControlProps: DateInputProps['getMonthControlProps'] = useCallback(
    (date: DateStringValue) => {
      const base = propsGetMonthControlProps?.(date) ?? {};
      const d = dayjs(date);
      if (d.month() === dayjs().month() && d.year() === dayjs().year()) {
        return {
          ...base,
          style: {
            ...base.style,
            fontWeight: 700,
            color: 'var(--mantine-color-blue-filled)',
          },
        };
      }
      return base;
    },
    [propsGetMonthControlProps]
  );

  const getYearControlProps: DateInputProps['getYearControlProps'] = useCallback(
    (date: DateStringValue) => {
      const base = propsGetYearControlProps?.(date) ?? {};
      const d = dayjs(date);
      if (d.year() === dayjs().year()) {
        return {
          ...base,
          style: {
            ...base.style,
            fontWeight: 700,
            color: 'var(--mantine-color-blue-filled)',
          },
        };
      }
      return base;
    },
    [propsGetYearControlProps]
  );

  /**
   * Strict-парсер: возвращает строку YYYY-MM-DD только для полного ввода DD.MM.YYYY.
   *
   * Важно возвращать именно строку (а не Date): Mantine DateInput передаёт результат
   * парсера в onChange как есть (см. setValue в DateInput.mjs). Возврат Date дал бы
   * в onChange локальную полночь, которая после JSON-сериализации через toISOString()
   * смещается на предыдущий день в положительных таймзонах.
   */
  const dateParser = useCallback((value: string): string | null => {
    const parsed = dayjs(value, 'DD.MM.YYYY', true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }, []);

  // Keep compatibility with legacy presets API (not supported by Mantine DateInput directly).
  void _propsPresets;

  return (
    <DateInput
      valueFormat='DD.MM.YYYY'
      dateParser={dateParser}
      {...rest}
      date={displayDate}
      onDateChange={handleDateChange}
      onLevelChange={handleLevelChange}
      monthsListFormat={monthsListFormat ?? 'MMM'}
      getDayProps={getDayProps}
      getMonthControlProps={getMonthControlProps}
      getYearControlProps={getYearControlProps}
      renderDay={propsRenderDay}
    />
  );
}
